import React, { useState, useEffect } from 'react';
import {
	Box, Typography, Button, Container, Paper, Grid,
	AppBar, Toolbar, useTheme, useMediaQuery, Chip, Divider, Stack, List,
	ListItem, ListItemIcon, ListItemText, Tabs, Tab,
	Menu, MenuItem, IconButton, Tooltip, CircularProgress
} from '@mui/material';
import {
	Security as SecurityIcon, Visibility as VisibilityIcon,
	Public as PublicIcon, CheckCircle as CheckIcon, AccountTree as TreeIcon,
	Layers as LayersIcon, AutoAwesome as AutoAwesomeIcon, Storage as StorageIcon,
	AccountBalanceWallet as WalletIcon, PlayArrow as PlayArrowIcon,
	CloudUpload as CloudUploadIcon, Group as GroupIcon,
	ContentCopy as CopyIcon, Logout as LogoutIcon,
	KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../contexts/WalletContext';
import { useSocialLinks } from '../../hooks/useSocialLinks';
import { usePlatformStats } from '../../hooks/usePlatformStats';
import SocialLinks from '../Common/SocialLinks';
import WalletModal from '../Wallet/WalletModal';
import NetworkSelector from '../Wallet/NetworkSelector';
import SEO from '../SEO';
import { startPriceRefresh } from '../../utils/cryptoPrices';
import { theme, commonStyles } from '../../styles/theme';
import { initVersionCheck, getCurrentVersion, forceRefresh } from '../../utils/versionCheck';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getConfig } from '../../lib/publicConfig';

// Import tab components
import FeaturesTab from './Home/FeaturesTab';
import HowItWorksTab from './Home/HowItWorksTab';
import PricingTab from './Home/PricingTab';
import FAQTab from './Home/FAQTab';
import DocumentationTab from './Home/DocumentationTab';

const LOGO_URL = 'https://images.solanadeads.com/graveforge.png';
const USD_PRICE_PER_TOKEN = getConfig('pricing.per_nft_usd', 0.20);

function TabPanel({ children, value, index }) {
	if (value !== index) {
		return null;
	}
	return (
		<Box role="tabpanel" sx={{ minHeight: 'calc(100vh - 200px)' }}>
			<Box>{children}</Box>
		</Box>
	);
}

const HomePage = () => {
	const navigate = useNavigate();
	const muiTheme = useTheme();
	const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
	const { connected, walletAddress, disconnect, walletType, walletName, jwtToken, authenticating, authError, connecting } = useWallet();
	const { socialLinks } = useSocialLinks();
	const platformStats = usePlatformStats() || {};
	const [activeTab, setActiveTab] = useState(0);
	const [conversionRates, setConversionRates] = useState({ SOL: 0, DEADS: 0, ETH: 0 });
	const [pricesError, setPricesError] = useState(null);
	const [walletModalOpen, setWalletModalOpen] = useState(false);
	const [walletMenuAnchor, setWalletMenuAnchor] = useState(null);
	const [copied, setCopied] = useState(false);
	const [selectedNetwork, setSelectedNetwork] = useState('solana');
	const [pricesLoading, setPricesLoading] = useState(true);
	const [scrolled, setScrolled] = useState(false);
	const { showInfo } = useSnackbar();
	// Check if we might have a returning session (stored wallet/JWT) to avoid homepage flash
	const [checkingSession, setCheckingSession] = useState(() => {
		return !!localStorage.getItem('boneforge_jwt') || !!localStorage.getItem('boneforge-wallet');
	});

	// Handle scroll effect
	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 50);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Debug logging (dev only)
	useEffect(() => {
		if (import.meta.env.DEV) {
			console.log('🔍 HomePage Wallet State:', {
				connected,
				walletAddress,
				walletType,
				walletName,
				jwtToken: jwtToken ? 'present' : 'missing',
				authenticating
			});
		}
	}, [connected, walletAddress, walletType, walletName, jwtToken, authenticating]);

	// Navigate to dashboard when wallet is connected and JWT is obtained
	useEffect(() => {
		if (connected && walletAddress && jwtToken && !authenticating) {
			if (import.meta.env.DEV) console.log('✅ Wallet connected and JWT obtained, navigating to dashboard');
			navigate('/app/dashboard');
			return;
		}
		// If we were checking for a returning session but autoConnect didn't reconnect,
		// stop waiting and show the homepage
		if (checkingSession && !connected && !connecting && !authenticating) {
			// Give autoConnect a moment to kick in, then show homepage
			const timer = setTimeout(() => {
				setCheckingSession(false);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [connected, walletAddress, jwtToken, authenticating, connecting, checkingSession, navigate]);

	const handleCopyAddress = () => {
		if (walletAddress) {
			navigator.clipboard.writeText(walletAddress);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const handleDisconnect = () => {
		disconnect();
		setWalletMenuAnchor(null);
	};

	// Initialize version checking
	useEffect(() => {
		initVersionCheck(() => {
			showInfo('New version available! Click to update.', {
				action: {
					label: 'Update',
					onClick: () => forceRefresh()
				},
				autoHideDuration: null // Don't auto-hide
			});
		});
		if (import.meta.env.DEV) console.log('🔄 Version check initialized');
	}, [showInfo]);

	// Fetch live crypto prices
	useEffect(() => {
		const cleanup = startPriceRefresh((prices) => {
			setConversionRates({
				SOL: prices.SOL || 0,
				ETH: prices.ETH || 0,
				DEADS: prices.CUSTOM || 0
			});
			setPricesError(prices.error || null);
			setPricesLoading(false);
		}, 60000); // Refresh every 60 seconds

		return cleanup;
	}, []);

	const handleConnectWallet = () => {
		setWalletModalOpen(true);
	};

	// Log JWT auth failure for debugging
	useEffect(() => {
		if (connected && walletAddress && !jwtToken && !authenticating && authError) {
			if (import.meta.env.DEV) console.warn('⚠️ Wallet connected but JWT authentication failed:', authError);
			// Auth failed, stop blocking the homepage
			setCheckingSession(false);
		}
	}, [connected, walletAddress, jwtToken, authenticating, authError]);

	const statsLoading = platformStats?.loading;

	const formatStatValue = (value, precisionLarge = 2) => {
		if (value === null || value === undefined || value === 0) {
			return '—';
		}

		// For very large numbers, show full count with commas (up to 6 significant digits)
		const absValue = Math.abs(value);
		
		// Quadrillion and above - show XXX,XXX,XXX format
		if (absValue >= 1000000000000000) { // Quadrillion (10^15)
			const quadrillions = Math.floor(value / 1000000000000000);
			const trillions = Math.floor((value % 1000000000000000) / 1000000000000);
			const billions = Math.floor((value % 1000000000000) / 1000000000);
			return `${quadrillions.toLocaleString()},${trillions.toString().padStart(3, '0')},${billions.toString().padStart(3, '0')}Q`;
		}
		
		// Trillion - show XXX,XXX,XXX format (trillions, billions, millions)
		if (absValue >= 1000000000000) { // Trillion (10^12)
			const trillions = Math.floor(value / 1000000000000);
			const billions = Math.floor((value % 1000000000000) / 1000000000);
			const millions = Math.floor((value % 1000000000) / 1000000);
			return `${trillions.toLocaleString()},${billions.toString().padStart(3, '0')},${millions.toString().padStart(3, '0')}T`;
		}
		
		// Billion - show XXX,XXX,XXX format (billions, millions, thousands)
		if (absValue >= 1000000000) { // Billion (10^9)
			const billions = Math.floor(value / 1000000000);
			const millions = Math.floor((value % 1000000000) / 1000000);
			const thousands = Math.floor((value % 1000000) / 1000);
			return `${billions.toLocaleString()},${millions.toString().padStart(3, '0')},${thousands.toString().padStart(3, '0')}B`;
		}
		
		// Million - show XXX,XXX,XXX format (millions, thousands, ones)
		if (absValue >= 1000000) { // Million (10^6)
			const millions = Math.floor(value / 1000000);
			const thousands = Math.floor((value % 1000000) / 1000);
			const ones = Math.floor(value % 1000);
			return `${millions.toLocaleString()},${thousands.toString().padStart(3, '0')},${ones.toString().padStart(3, '0')}M`;
		}
		
		// Thousand - standard format
		if (absValue >= 1000) {
			return `${(value / 1000).toFixed(precisionLarge)}K`;
		}
		
		return value.toLocaleString();
	};

	const heroStats = [
		{
			label: 'NFTs Generated',
			value: formatStatValue(platformStats?.totalNFTsGenerated),
			description: 'Collections exported to chain',
			icon: <CloudUploadIcon sx={{ fontSize: 24, color: theme.colors.primary }} />
		},
		{
			label: 'Collections Built',
			value: formatStatValue(platformStats?.totalProjects),
			description: 'Studios active across GraveForge',
			icon: <StorageIcon sx={{ fontSize: 24, color: theme.colors.textPrimary }} />
		},
		{
			label: 'Avg Collection Size',
			value: formatStatValue(platformStats?.avgCollectionSize, { precisionLarge: 2 }),
			description: 'Configured across decision paths',
			icon: <LayersIcon sx={{ fontSize: 24, color: '#94a3b8' }} />
		},
		{
			label: 'Active Creators',
			value: formatStatValue(platformStats?.uniqueUsers),
			description: 'Teams collaborating in real time',
			icon: <GroupIcon sx={{ fontSize: 24, color: '#dc2626' }} />
		}
	];

	const heroInsightBullets = [
		'Create both static and animated NFT collections—generate MP4, WebM, GIF videos alongside traditional PNG and JPG images in a single project.',
		'Drag-and-drop visual builder with no coding required—build complex collections with thousands of variations, including mixed static and animated NFTs.',
		'Fast-lane generation with automatic duplicate mitigation, pixel-level watermarking, and intelligent per-NFT format detection.',
		'Deep decision-tree orchestration with conditional rules, reroll logic, and distribution analytics—all through an intuitive interface.'
	];

	const paymentCards = [
		{
			label: 'SOL',
			symbol: '◎',
			accent: '#14F195',
			value: conversionRates.SOL
		},
		{
			label: 'ETH',
			symbol: 'Ξ',
			accent: '#627EEA',
			value: conversionRates.ETH
		},
		{
			label: 'DEADS',
			symbol: '💀',
			accent: '#ff66ff',
			value: conversionRates.DEADS
		}
	];

	const formatFiatRate = (value) => {
		if (pricesLoading || value === undefined || value === null || Number.isNaN(Number(value)) || Number(value) <= 0) {
			return 'Syncing...';
		}

		const numeric = Number(value);
		const digits = numeric >= 1 ? 2 : 4;
		return `$${numeric.toLocaleString(undefined, {
			minimumFractionDigits: digits,
			maximumFractionDigits: digits
		})}`;
	};

	const heroHighlights = [
		{
			icon: <AutoAwesomeIcon sx={{ fontSize: 28, color: theme.colors.primary }} />,
			title: 'No-Code Visual Builder',
			description: 'Drag-and-drop interface with AI-powered rule generation from natural language—create complex collections without writing a single line of code.'
		},
		{
			icon: <PlayArrowIcon sx={{ fontSize: 28, color: '#dc2626' }} />,
			title: 'Animated & Static NFTs',
			description: 'Generate MP4, WebM, and GIF animated NFTs alongside PNG, JPG, and WebP images. Create mixed collections with both formats, or pure animated projects with customizable FPS, duration, and quality settings up to 8K resolution.'
		},
		{
			icon: <SecurityIcon sx={{ fontSize: 28, color: '#94a3b8' }} />,
			title: 'Pixel-Level Trust',
			description: 'Invisible steganography, audit trails, and wallet-gated security keep every drop verifiable.'
		},
		{
			icon: <PublicIcon sx={{ fontSize: 28, color: theme.colors.textPrimary }} />,
			title: 'Multi-Chain Export',
			description: 'Ship Solana, Ethereum, Base, and Abstract metadata in a single pass with automated validation and proper MIME type handling for video NFTs.'
		},
		{
			icon: <GroupIcon sx={{ fontSize: 28, color: '#dc2626' }} />,
			title: 'Delegated Workflows',
			description: 'Role-based permissions, live activity feeds, and duplicate prevention keep studios in sync.'
		}
	];

	// Show a minimal loading screen while checking if user has an existing session
	// This prevents the homepage from flashing before redirecting to dashboard
	if (checkingSession || authenticating) {
		return (
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100vh',
				gap: '24px',
				backgroundColor: '#000000'
			}}>
				<Box
					component="img"
					src={LOGO_URL}
					alt="GraveForge"
					sx={{
						width: 120,
						height: 'auto',
						animation: 'pulse 2s ease-in-out infinite',
						'@keyframes pulse': {
							'0%, 100%': { opacity: 1 },
							'50%': { opacity: 0.5 },
						},
					}}
				/>
				<CircularProgress sx={{ color: '#7d1f1f' }} size={32} />
			</Box>
		);
	}

	return (
		<>
			<SEO
				title="GraveForge - Animated NFT Generator | No-Code Static & Video NFT Creator"
				description="Create professional static and animated NFT collections with GraveForge's no-code platform. Generate MP4, WebM, GIF videos and PNG, JPG, WebP images. Visual drag-and-drop generator with mixed collection support, decision trees, rarity controls, and multi-chain export for Solana, Ethereum, and Polygon. Build animated PFPs and video collectibles without coding."
				keywords="animated NFT generator, video NFT creator, MP4 NFT, WebM NFT, GIF NFT, no-code NFT generator, NFT creator, NFT collection, generative art, mixed NFT collection, static and animated NFTs, Solana NFT, Ethereum NFT, Polygon NFT, NFT maker, NFT tool, blockchain art, crypto art, digital collectibles, NFT minting, NFT layers, NFT rarity, Metaplex, OpenSea, Candy Machine, no code, visual builder, animated PFP, video collectibles"
				image="https://images.solanadeads.com/graveforge.png"
				creator="@SOLANADEADS"
				type="website"
			/>
			<Box sx={{ 
				minHeight: '100vh', 
				bgcolor: '#000000',
				color: '#ffffff',
				position: 'relative',
				'&::before': {
					content: '""',
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: -1,
					background: 'linear-gradient(135deg, #000000 0%, #0d1117 50%, #000000 100%)',
				},
				'&::after': {
					content: '""',
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: -1,
					background: 'radial-gradient(circle at 20% 50%, rgba(220, 38, 38, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(153, 27, 27, 0.15) 0%, transparent 50%)',
					animation: 'pulse 8s ease-in-out infinite',
				},
				'@keyframes pulse': {
					'0%, 100%': { opacity: 0.5 },
					'50%': { opacity: 1 },
				},
			}}>
			{/* Sticky Header */}
			<AppBar position="sticky" sx={{ 
				bgcolor: scrolled ? 'rgba(0, 0, 0, 0.95)' : 'transparent',
				backdropFilter: scrolled ? 'blur(20px)' : 'none',
				boxShadow: scrolled ? '0 4px 30px rgba(220, 38, 38, 0.2)' : 'none',
				borderBottom: scrolled ? '1px solid rgba(220, 38, 38, 0.2)' : 'none',
				transition: 'all 0.3s',
				top: 0, 
				zIndex: 1100 
			}}>
				<Toolbar sx={{ py: 1, px: { xs: 1, sm: 2 } }}>
					<Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" sx={{ flexGrow: 1 }}>
						<Box
							component="img"
							src={LOGO_URL}
							alt="GraveForge"
							sx={{
								width: { xs: 32, sm: 40 },
								height: { xs: 32, sm: 40 },
								filter: 'drop-shadow(0 0 10px rgba(220, 38, 38, 0.5))',
							}}
						/>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 900,
								fontSize: { xs: '1rem', sm: '1.25rem' },
								background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
								display: { xs: 'none', sm: 'block' },
								textShadow: '0 0 30px rgba(220, 38, 38, 0.5)',
							}}
						>
							GraveForge
						</Typography>
					</Stack>
					<Box sx={{ display: { xs: 'none', md: 'block' }, mr: 2 }}>
						<SocialLinks links={socialLinks} color="text.secondary" size="small" spacing={1} />
					</Box>
					
					{/* Network Selector - Hidden on mobile when connected */}
					<Box sx={{ mr: { xs: 0.5, sm: 2 }, display: { xs: connected ? 'none' : 'block', sm: 'block' } }}>
						<NetworkSelector 
							selectedNetwork={selectedNetwork} 
							onNetworkChange={setSelectedNetwork} 
						/>
					</Box>
					
					{connected ? (
						<Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
							<Button
								variant="outlined"
								onClick={(e) => setWalletMenuAnchor(e.currentTarget)}
								endIcon={!isMobile && <ArrowDownIcon />}
								sx={{
									borderRadius: 2,
									px: { xs: 1, sm: 2 },
									py: 1,
									minWidth: { xs: 'auto', sm: 'auto' },
									borderColor: walletType === 'solana' ? 'rgba(20, 241, 149, 0.5)' : 'rgba(98, 126, 234, 0.5)',
									color: walletType === 'solana' ? '#14F195' : '#627EEA',
									'&:hover': {
										borderColor: walletType === 'solana' ? '#14F195' : '#627EEA',
										bgcolor: walletType === 'solana' ? 'rgba(20, 241, 149, 0.1)' : 'rgba(98, 126, 234, 0.1)',
									}
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
									<Box sx={{
										width: 24,
										height: 24,
										borderRadius: '50%',
										bgcolor: walletType === 'solana' ? 'rgba(20, 241, 149, 0.2)' : 'rgba(98, 126, 234, 0.2)',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: '14px',
										fontWeight: 'bold'
									}}>
										{walletType === 'solana' ? '◎' : 'Ξ'}
									</Box>
									<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', display: { xs: 'none', sm: 'block' } }}>
										{walletAddress?.slice(0, 4)}...{walletAddress?.slice(-4)}
									</Typography>
									<Chip
										label={walletType === 'solana' ? 'Solana' : 'Ethereum'}
										size="small"
										sx={{
											height: 20,
											fontSize: '0.7rem',
											display: { xs: 'none', sm: 'inline-flex' },
											bgcolor: walletType === 'solana' ? 'rgba(20, 241, 149, 0.2)' : 'rgba(98, 126, 234, 0.2)',
											color: walletType === 'solana' ? '#14F195' : '#627EEA',
										}}
									/>
								</Box>
							</Button>
							<Button 
								variant="contained" 
								onClick={() => {
									if (jwtToken) {
										navigate('/app/dashboard');
									} else {
										if (import.meta.env.DEV) console.warn('⚠️ Cannot navigate to dashboard without JWT authentication');
									}
								}}
								startIcon={!isMobile && <PlayArrowIcon />}
								sx={{ 
									borderRadius: 2, 
									px: { xs: 2, sm: 3 },
									fontSize: { xs: '0.875rem', sm: '1rem' },
									background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
									boxShadow: `0 4px 20px ${theme.colors.primaryAlpha50}`,
									'&:hover': {
										background: `linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.primary} 100%)`,
										boxShadow: `0 6px 30px ${theme.colors.borderPrimary}`,
										transform: 'translateY(-2px)',
									},
									transition: 'all 0.3s',
								}}
							>
								{isMobile ? 'App' : 'Dashboard'}
							</Button>
							
							{/* Wallet Menu */}
							<Menu
								anchorEl={walletMenuAnchor}
								open={Boolean(walletMenuAnchor)}
								onClose={() => setWalletMenuAnchor(null)}
								PaperProps={{
									sx: {
										bgcolor: 'rgba(0, 0, 0, 0.95)',
										border: `1px solid ${theme.colors.borderPrimary}`,
										minWidth: 250
									}
								}}
							>
								<Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(39, 48, 63, 0.5)' }}>
									<Typography variant="caption" color="text.secondary">
										Connected Wallet
									</Typography>
									<Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.colors.primary }}>
										{walletName || 'Unknown Wallet'}
									</Typography>
									<Typography variant="caption" sx={{ fontFamily: 'monospace', color: theme.colors.textSecondary }}>
										{walletAddress}
									</Typography>
								</Box>
								<MenuItem onClick={handleCopyAddress}>
									<ListItemIcon>
										<CopyIcon fontSize="small" sx={{ color: copied ? '#00ff00' : 'inherit' }} />
									</ListItemIcon>
									<ListItemText>{copied ? 'Copied!' : 'Copy Address'}</ListItemText>
								</MenuItem>
								<MenuItem onClick={handleDisconnect}>
									<ListItemIcon>
										<LogoutIcon fontSize="small" sx={{ color: '#f44336' }} />
									</ListItemIcon>
									<ListItemText>Disconnect</ListItemText>
								</MenuItem>
							</Menu>
						</Stack>
					) : (
						<Button 
							variant="contained" 
							onClick={handleConnectWallet}
							startIcon={!isMobile && <WalletIcon />}
							sx={{ 
								borderRadius: 2, 
								px: { xs: 2, sm: 3 },
								fontSize: { xs: '0.875rem', sm: '1rem' },
								background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
								boxShadow: '0 4px 20px rgba(220, 38, 38, 0.4)',
								'&:hover': {
									background: 'linear-gradient(135deg, #ef4444 0%, #991b1b 100%)',
									boxShadow: '0 6px 30px rgba(220, 38, 38, 0.6)',
									transform: 'translateY(-2px)',
								},
								transition: 'all 0.3s',
							}}
						>
							{isMobile ? 'Connect' : 'Connect Wallet'}
						</Button>
					)}
				</Toolbar>

				{/* Navigation Tabs */}
				<Box sx={{ borderTop: `1px solid ${theme.colors.borderSubtle}` }}>
					<Container maxWidth="lg">
						<Tabs
							value={activeTab}
							onChange={(e, v) => setActiveTab(v)}
							variant={isMobile ? 'scrollable' : 'standard'}
							scrollButtons="auto"
							sx={{
								'& .MuiTab-root': {
									minHeight: 48,
									textTransform: 'none',
									fontWeight: 600,
									fontSize: '0.9rem',
									color: theme.colors.textMuted,
									transition: 'color 0.2s ease',
									'&.Mui-selected': {
										color: theme.colors.textPrimary,
									},
									'&:hover': {
										color: theme.colors.textSecondary,
									},
								},
								'& .MuiTabs-indicator': {
									backgroundColor: theme.colors.primary,
									height: 2,
								},
							}}
						>
							<Tab label="Overview" />
							<Tab label="Features" />
							<Tab label="How It Works" />
							<Tab label="Pricing" />
							<Tab label="FAQ" />
							<Tab label="Documentation" />
						</Tabs>
					</Container>
				</Box>
			</AppBar>

			{/* Tab Content */}
			{/* Overview Tab */}
			<TabPanel value={activeTab} index={0}>
				<Box sx={{ py: { xs: 4, md: 8 } }}>
					<Container maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: { xs: 2, sm: 3 } }}>
						<Box textAlign="center" mb={{ xs: 4, md: 6 }} sx={{ width: '100%', maxWidth: 960 }}>
							<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3.5 }, py: { xs: 1.5, sm: 2.5 }, borderRadius: 999, border: `1px solid ${theme.colors.borderPrimary}`, bgcolor: 'rgba(220,38,38,0.08)', boxShadow: '0 0 24px rgba(220, 38, 38, 0.25)' }}>
								<Box sx={{ width: { xs: 8, sm: 12 }, height: { xs: 8, sm: 12 }, borderRadius: '50%', background: 'linear-gradient(135deg, #dc2626, #ef4444)', boxShadow: '0 0 12px rgba(220,38,38,0.6)' }} />
								<Typography variant="subtitle2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, letterSpacing: 1.2, fontWeight: 700, textTransform: 'uppercase', color: theme.colors.primary }}>
									Solana Deads Production-Ready
								</Typography>
							</Box>
							<Typography variant={isMobile ? 'h3' : 'h1'} component="h1" gutterBottom sx={{ 
								fontWeight: 900, 
								mb: { xs: 2, md: 3 }, 
								fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' },
								lineHeight: 1.1,
								background: 'linear-gradient(135deg, #ffffff 0%, #ef4444 45%, #991b1b 100%)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
								textShadow: '0 0 80px rgba(220, 38, 38, 0.4)',
							}}>
								Animated & Static NFT Generator for Generative Powerhouses
						</Typography>
						{statsLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 4, md: 5 } }}>
								<CircularProgress sx={{ color: theme.colors.primary }} />
							</Box>
						) : (
							<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
								<Box sx={{ width: '100%', maxWidth: 820, mx: 'auto', mb: { xs: 4, md: 5 } }}>
									<Box sx={{ 
										p: 4, 
										bgcolor: 'rgba(39, 48, 63, 0.4)', 
										borderRadius: 3, 
										border: `1px solid ${theme.colors.borderPrimary}`, 
										textAlign: 'center',
										position: 'relative',
										overflow: 'hidden',
										'&::before': {
											content: '""',
											position: 'absolute',
											top: '-50%',
											left: '-50%',
											width: '200%',
											height: '200%',
											background: 'radial-gradient(circle, rgba(220, 38, 38, 0.1) 0%, transparent 70%)',
											animation: 'rotate 20s linear infinite',
										},
										'@keyframes rotate': {
											'0%': { transform: 'rotate(0deg)' },
											'100%': { transform: 'rotate(360deg)' },
										},
									}}>
										<Box sx={{ position: 'relative', zIndex: 1 }}>
											<AutoAwesomeIcon sx={{ fontSize: 48, color: theme.colors.primary, mb: 2 }} />
											<Typography variant="h3" sx={{ 
												fontWeight: 900, 
												background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
												WebkitBackgroundClip: 'text',
												WebkitTextFillColor: 'transparent',
												mb: 1,
												fontFamily: 'monospace',
											}}>
												{(() => {
													const combinations = platformStats?.totalPossibleCombinations || 0;
													
													// Quadrillion and above
													if (combinations >= 1000000000000000) {
														const quadrillions = Math.floor(combinations / 1000000000000000);
														const trillions = Math.floor((combinations % 1000000000000000) / 1000000000000);
														const billions = Math.floor((combinations % 1000000000000) / 1000000000);
														return `${quadrillions.toLocaleString()},${trillions.toString().padStart(3, '0')},${billions.toString().padStart(3, '0')}Q+`;
													}
													
													// Trillion
													if (combinations >= 1000000000000) {
														const trillions = Math.floor(combinations / 1000000000000);
														const billions = Math.floor((combinations % 1000000000000) / 1000000000);
														const millions = Math.floor((combinations % 1000000000) / 1000000);
														return `${trillions.toLocaleString()},${billions.toString().padStart(3, '0')},${millions.toString().padStart(3, '0')}T+`;
													}
													
													// Billion
													if (combinations >= 1000000000) {
														const billions = Math.floor(combinations / 1000000000);
														const millions = Math.floor((combinations % 1000000000) / 1000000);
														const thousands = Math.floor((combinations % 1000000) / 1000);
														return `${billions.toLocaleString()},${millions.toString().padStart(3, '0')},${thousands.toString().padStart(3, '0')}B+`;
													}
													
													// Million
													if (combinations >= 1000000) {
														const millions = Math.floor(combinations / 1000000);
														const thousands = Math.floor((combinations % 1000000) / 1000);
														const ones = Math.floor(combinations % 1000);
														return `${millions.toLocaleString()},${thousands.toString().padStart(3, '0')},${ones.toString().padStart(3, '0')}M+`;
													}
													
													// Thousand
													if (combinations >= 1000) return `${(combinations / 1000).toFixed(1)}K+`;
													
													return combinations.toLocaleString();
												})()}
											</Typography>
											<Typography variant="h6" sx={{ color: theme.colors.primary, fontWeight: 700, mb: 1 }}>
												Potential NFT Combinations
											</Typography>
											<Typography variant="body2" sx={{ color: theme.colors.textSecondary }}>
												Total unique NFTs that could be generated across all projects based on layer configurations
											</Typography>
										</Box>
									</Box>
								</Box>
								<Grid
									container
									spacing={2.5}
									justifyContent="center"
									sx={{ mb: 3, width: '100%', maxWidth: 960 }}
								>
									{heroStats.map((stat, index) => (
										<Grid item xs={12} sm={6} md={3} key={stat.label || index}>
											<Paper
												sx={{
													...commonStyles.card,
													p: { xs: 2.5, md: 3 },
													height: '100%',
													textAlign: 'left',
													border: `1px solid ${theme.colors.borderPrimary}`,
													bgcolor: 'rgba(17,17,17,0.85)'
												}}
											>
												<Stack spacing={1.5}>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
														<Box
															sx={{
																width: 44,
																height: 44,
																borderRadius: '16px',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																background: 'linear-gradient(135deg, rgba(220,38,38,0.18) 0%, rgba(220,38,38,0.05) 100%)',
																border: `1px solid ${theme.colors.borderPrimary}`
															}}
														>
															{stat.icon}
														</Box>
														<Typography variant="overline" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.74)', letterSpacing: 1 }}>
															{stat.label}
														</Typography>
													</Box>
													<Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontFamily: 'Space Grotesk, monospace' }}>
														{stat.value}
													</Typography>
													<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)' }}>
														{stat.description}
													</Typography>
												</Stack>
											</Paper>
										</Grid>
									))}
								</Grid>
								<Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', mb: 3 }}>
									<Grid container spacing={2} justifyContent="center">
										<Grid item xs={6} sm={4} md={2.4}>
											<Box sx={{ 
												textAlign: 'center', 
												p: 2, 
												bgcolor: 'rgba(220, 38, 38, 0.05)', 
												borderRadius: 2, 
												border: '1px solid rgba(255,255,255,0.1)', 
												transition: 'all 0.3s',
												'&:hover': {
													bgcolor: 'rgba(39, 48, 63, 0.4)',
													borderColor: '#dc2626',
													transform: 'translateY(-2px)',
												}
											}}>
												<Typography variant="h5" sx={{ fontWeight: 700, color: theme.colors.primary }}>
													{(platformStats?.totalLayers || 0).toLocaleString()}
												</Typography>
												<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Layers</Typography>
											</Box>
										</Grid>
										<Grid item xs={6} sm={4} md={2.4}>
											<Box sx={{ 
												textAlign: 'center', 
												p: 2, 
												bgcolor: 'rgba(220, 38, 38, 0.05)', 
												borderRadius: 2, 
												border: '1px solid rgba(255,255,255,0.1)', 
												transition: 'all 0.3s',
												'&:hover': {
													bgcolor: 'rgba(39, 48, 63, 0.4)',
													borderColor: '#dc2626',
													transform: 'translateY(-2px)',
												}
											}}>
												<Typography variant="h5" sx={{ fontWeight: 700, color: theme.colors.primary }}>
													{(platformStats?.totalRules || 0).toLocaleString()}
												</Typography>
												<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Rules</Typography>
											</Box>
										</Grid>
										<Grid item xs={6} sm={4} md={2.4}>
											<Box sx={{ 
												textAlign: 'center', 
												p: 2, 
												bgcolor: 'rgba(220, 38, 38, 0.05)', 
												borderRadius: 2, 
												border: '1px solid rgba(255,255,255,0.1)', 
												transition: 'all 0.3s',
												'&:hover': {
													bgcolor: 'rgba(39, 48, 63, 0.4)',
													borderColor: '#dc2626',
													transform: 'translateY(-2px)',
												}
											}}>
												<Typography variant="h5" sx={{ fontWeight: 700, color: theme.colors.primary }}>
													{(platformStats?.totalPaths || 0).toLocaleString()}
												</Typography>
												<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Paths</Typography>
											</Box>
										</Grid>
										<Grid item xs={6} sm={4} md={2.4}>
											<Box sx={{ 
												textAlign: 'center', 
												p: 2, 
												bgcolor: 'rgba(220, 38, 38, 0.05)', 
												borderRadius: 2, 
												border: '1px solid rgba(255,255,255,0.1)', 
												transition: 'all 0.3s',
												'&:hover': {
													bgcolor: 'rgba(39, 48, 63, 0.4)',
													borderColor: '#dc2626',
													transform: 'translateY(-2px)',
												}
											}}>
												<Typography variant="h5" sx={{ fontWeight: 700, color: theme.colors.primary }}>
													{platformStats?.avgLayersPerProject || 0}
												</Typography>
												<Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Avg Layers</Typography>
											</Box>
										</Grid>
										<Grid item xs={6} sm={4} md={2.4}>
											<Box sx={{ 
												textAlign: 'center', 
												p: 2, 
												bgcolor: 'rgba(220, 38, 38, 0.05)', 
												borderRadius: 2, 
												border: '1px solid rgba(255,255,255,0.1)', 
												transition: 'all 0.3s',
												'&:hover': {
													bgcolor: 'rgba(39, 48, 63, 0.4)',
													borderColor: '#dc2626',
													transform: 'translateY(-2px)',
												}
											}}>
												<Typography variant="h5" sx={{ fontWeight: 700, color: theme.colors.primary }}>
													{(platformStats?.avgCollectionSize || 0) >= 1000 
														? `${((platformStats?.avgCollectionSize || 0) / 1000).toFixed(1)}K` 
														: (platformStats?.avgCollectionSize || 0)}
												</Typography>
											</Box>
										</Grid>
									</Grid>
								</Box>
								<Typography
									variant="h5"
									sx={{
										color: 'rgba(255,255,255,0.78)',
										maxWidth: 960,
										mx: 'auto',
										mb: 5,
										fontWeight: 400,
										lineHeight: 1.7,
										textAlign: 'center'
									}}
								>
									Launch cinematic animated and static NFT universes without writing code. Generate MP4, WebM, GIF videos and high-resolution images with visual interface, live telemetry, deterministic rule paths, and automated QA built for Solana Deads–scale drops.
								</Typography>
							</Box>
						)}
						</Box>
					</Container>
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
					<Container maxWidth="lg">
						<Grid container spacing={4} alignItems="stretch">
							<Grid item xs={12} md={7}>
								<Paper sx={{ 
									...commonStyles.card,
									p: { xs: 3, sm: 4 },
									height: '100%',
									border: `1px solid ${theme.colors.borderPrimary}`,
									background: 'rgba(17,17,17,0.8)'
								}}>
									<Stack spacing={{ xs: 3, md: 4 }}>
										<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-start">
											<Button 
												variant="contained" 
												size="large" 
												onClick={connected ? () => {
													if (jwtToken) {
														navigate('/app/dashboard');
													} else {
														if (import.meta.env.DEV) console.warn('⚠️ Cannot navigate to dashboard without JWT authentication');
													}
												} : handleConnectWallet} 
												startIcon={connected ? <PlayArrowIcon /> : <WalletIcon />}
												fullWidth={isMobile}
												sx={{ 
													px: { xs: 4, sm: 6 }, 
													py: { xs: 2, sm: 2.5 }, 
													fontSize: { xs: '1rem', sm: '1.2rem' }, 
													borderRadius: 3,
													background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
													boxShadow: `0 8px 32px ${theme.colors.primaryAlpha50}`,
													'&:hover': {
														background: `linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.primary} 100%)`,
														boxShadow: `0 12px 48px ${theme.colors.borderPrimary}`,
														transform: 'translateY(-4px)'
													},
													transition: 'all 0.3s'
												}}
											>
												{connected ? 'Launch Dashboard' : 'Connect & Start Building'}
											</Button>
											<Button 
												variant="outlined" 
												size="large" 
												onClick={() => setActiveTab(1)} 
												startIcon={<AutoAwesomeIcon />}
												fullWidth={isMobile}
												sx={{ 
													px: { xs: 4, sm: 6 }, 
													py: { xs: 2, sm: 2.5 }, 
													fontSize: { xs: '1rem', sm: '1.2rem' }, 
													borderRadius: 3,
													borderWidth: 2,
													borderColor: '#dc2626',
													color: theme.colors.primary,
													'&:hover': {
														borderWidth: 2,
														borderColor: '#ef4444',
														bgcolor: 'rgba(39, 48, 63, 0.4)',
														transform: 'translateY(-4px)',
														boxShadow: '0 8px 32px rgba(220, 38, 38, 0.3)',
													},
													transition: 'all 0.3s'
												}}
											>
												Explore Features
											</Button>
										</Stack>
										<List disablePadding>
											{heroInsightBullets.map((bullet, index) => (
												<ListItem key={index} sx={{ px: 0, py: 1.2 }}>
													<ListItemIcon sx={{ minWidth: 36 }}>
														<CheckIcon sx={{ color: theme.colors.primary }} />
													</ListItemIcon>
													<ListItemText
														primary={bullet}
														primaryTypographyProps={{ color: 'rgba(255,255,255,0.7)', variant: 'body1', lineHeight: 1.6 }}
													/>
												</ListItem>
											))}
										</List>
										<Box>
											<Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 1, display: 'block', mb: 1 }}>
												Live Conversion Snapshots
											</Typography>
											<Grid container spacing={1.5}>
												{paymentCards.map((card) => (
													<Grid item xs={12} sm={4} key={card.label}>
														<Paper sx={{ 
															...commonStyles.card,
															p: 2,
															display: 'flex',
															alignItems: 'center',
															gap: 1.5,
															border: `1px solid rgba(255,255,255,0.12)`
														}}>
															<Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `${card.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
																{card.symbol}
															</Box>
															<Box>
																<Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700 }}>{card.label}</Typography>
																<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Updated every 60s</Typography>
															</Box>
															<Typography variant="subtitle1" sx={{ marginLeft: 'auto', fontWeight: 700, color: card.accent }}>
																{formatFiatRate(card.value)}
															</Typography>
														</Paper>
													</Grid>
												))}
											</Grid>
										</Box>
									</Stack>
								</Paper>
							</Grid>
							<Grid item xs={12} md={5}>
								<Stack spacing={2.5}>
									{heroHighlights.map((item, index) => (
										<Paper
											key={index}
											sx={{
												...commonStyles.card,
												p: 3,
												border: `1px solid ${theme.colors.borderPrimary}`,
												background: 'linear-gradient(145deg, rgba(26,0,0,0.85) 0%, rgba(10,10,10,0.9) 100%)',
												position: 'relative',
												overflow: 'hidden',
												'&::after': {
													content: '""',
													position: 'absolute',
													top: -60,
													right: -60,
													width: 160,
													height: 160,
													borderRadius: '50%',
													background: 'radial-gradient(circle, rgba(220,38,38,0.18) 0%, transparent 65%)'
												},
												'&:hover': {
													borderColor: '#dc2626',
													transform: 'translateY(-6px)',
													boxShadow: '0 20px 48px rgba(220,38,38,0.35)'
												}
											}}
										>
											<Stack spacing={2}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
													<Box
														sx={{
															width: 48,
															height: 48,
															borderRadius: 2,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															bgcolor: 'rgba(255,255,255,0.05)',
															border: `1px solid ${theme.colors.borderPrimary}`
														}}
													>
														{React.cloneElement(item.icon, { sx: { fontSize: 30, color: theme.colors.primary } })}
													</Box>
													<Box>
														<Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
															{item.title}
														</Typography>
														<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
															{item.description}
														</Typography>
													</Box>
												</Box>
											</Stack>
										</Paper>
									))}
								</Stack>
							</Grid>
						</Grid>
					</Container>
				</Box>
				{/* Comparison Table */}
				<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: { xs: 6, md: 8 } }}>
					<Container maxWidth="lg">
						<Box mb={{ xs: 4, md: 6 }}>
							<Typography variant="h4" align="center" gutterBottom sx={{ 
								fontWeight: 800, 
								fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
								mb: { xs: 3, md: 4 }, 
								background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
							}}>
								Why Choose GraveForge?
							</Typography>
							<Paper elevation={0} sx={{ 
								border: '1px solid', 
								borderColor: theme.colors.borderSubtle, 
								borderRadius: 4, 
								overflow: 'hidden',
								bgcolor: theme.colors.bgGlass,
								backdropFilter: 'blur(10px)',
							}}>
								<Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
									<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
										<Box component="thead" sx={{ bgcolor: 'rgba(39, 48, 63, 0.4)' }}>
											<Box component="tr">
												<Box component="th" sx={{ p: 2, textAlign: 'left', borderBottom: '1px solid', borderColor: theme.colors.borderSubtle }}>
													<Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.colors.primary }}>Feature</Typography>
												</Box>
												<Box component="th" sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid', borderColor: theme.colors.borderSubtle, bgcolor: 'rgba(39, 48, 63, 0.5)' }}>
													<Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.colors.primary }}>GraveForge</Typography>
												</Box>
												<Box component="th" sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid', borderColor: theme.colors.borderSubtle }}>
													<Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.colors.textSecondary }}>Others</Typography>
												</Box>
											</Box>
										</Box>
										<Box component="tbody">
											{[
												{ feature: 'Decision Tree Generation', boneforge: true, others: false },
												{ feature: 'Live Preview', boneforge: true, others: true },
												{ feature: 'Smart Rules Engine', boneforge: true, others: false },
												{ feature: 'Multi-Chain Support', boneforge: true, others: false },
												{ feature: 'Pay-Per-Export', boneforge: true, others: false },
												{ feature: 'Unlimited Projects', boneforge: true, others: false }
											].map((row, idx) => (
												<Box component="tr" key={idx} sx={{ 
													'&:hover': { bgcolor: 'rgba(220, 38, 38, 0.05)' },
													transition: 'all 0.2s',
												}}>
													<Box component="td" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'rgba(39, 48, 63, 0.4)' }}>
														<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{row.feature}</Typography>
													</Box>
													<Box component="td" sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid', borderColor: 'rgba(39, 48, 63, 0.4)', bgcolor: 'rgba(39, 48, 63, 0.4)' }}>
														{row.boneforge ? <CheckIcon sx={{ color: '#00ff00' }} /> : <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>—</Typography>}
													</Box>
													<Box component="td" sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid', borderColor: 'rgba(39, 48, 63, 0.4)' }}>
														{row.others ? <CheckIcon sx={{ color: '#00ff00' }} /> : <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>—</Typography>}
													</Box>
												</Box>
											))}
										</Box>
									</Box>
								</Box>
							</Paper>
						</Box>
					</Container>
				</Box>
			</TabPanel>


		{/* Features Tab */}
		<TabPanel value={activeTab} index={1}>
			<FeaturesTab />
		</TabPanel>

		{/* How It Works Tab */}
		<TabPanel value={activeTab} index={2}>
			<HowItWorksTab />
		</TabPanel>

		{/* Pricing Tab */}
		<TabPanel value={activeTab} index={3}>
			<PricingTab conversionRates={conversionRates} pricesLoading={pricesLoading} pricesError={pricesError} />
		</TabPanel>

		{/* FAQ Tab */}
		<TabPanel value={activeTab} index={4}>
			<FAQTab />
		</TabPanel>

		{/* Documentation Tab */}
		<TabPanel value={activeTab} index={5}>
			<DocumentationTab />
		</TabPanel>

		{/* Footer */}
		<Box sx={{ bgcolor: theme.colors.bgCard, borderTop: '1px solid', borderColor: 'divider', py: 4, mt: 8 }}>
			<Container maxWidth="lg">
				<Stack spacing={2} alignItems="center">
					<SocialLinks links={socialLinks} color="text.secondary" size="medium" spacing={1.5} />
					<Typography variant="body2" color="text.secondary" align="center">
						{new Date().getFullYear()} GraveForge — No-Code NFT Collection Generator. All rights reserved.
					</Typography>
					<Typography variant="body2" color="text.secondary" align="center">
						Powered by <strong>Solana Deads</strong>.
					</Typography>
				</Stack>
			</Container>
		</Box>
		
		{/* Wallet Connection Modal */}
		<WalletModal 
			open={walletModalOpen} 
			onClose={() => setWalletModalOpen(false)}
			selectedNetwork={selectedNetwork}
		/>
	</Box>
			</>
		);
	};

	export default HomePage;
